<template>
  <v-app
    id="webwalker"
    :class="{ 'is-auth': isAuthView }"
  >
    <!-- Splash overlay: brief fade on first paint (look unchanged). -->
    <v-sheet
      v-show="!loaded"
      class="pa-3"
      :color="isAuthView ? 'blue-darken-3' : (isDarkMode ? 'grey-darken-4' : 'white')"
      style="z-index: 100; width: 100%; height: 100%; position: absolute; transition: opacity cubic-bezier(0, 0.52, 1, 1) 0.25s;"
      :style="{ opacity: loading ? 1 : 0 }"
    >
      <div
        class="pb-6"
        style="display: flex; align-items: center; justify-content: center; height: 100%"
      >
        <v-progress-circular
          :size="70"
          :width="3"
          :color="isAuthView ? 'white' : 'blue-darken-3'"
          indeterminate
        ></v-progress-circular>
      </div>
    </v-sheet>
    <main-nav
      v-if="isTopLayout && !$store.state.isMaintenance && !isSidebarLayout"
      @logout="logout()"
    ></main-nav>

    <main-nav-sidebar
      v-if="isSidebarLayout"
      @logout="logout()"
    ></main-nav-sidebar>

    <v-main
      class="pt-12"
      :class="{ 'background': !isAuthView, 'blue-darken-3': isAuthView }"
    >
      <transition-container>
        <router-view></router-view>
      </transition-container>
    </v-main>
  </v-app>
</template>

<script>
import { pushAxiosErrors } from '@/helpers/axiosErrors';
import AuthApi from '@/api/AuthApi';
import MainNav from '@/components/layout/MainNav.vue';
import MainNavSidebar from '@/components/layout/MainNavSidebar.vue';
import TransitionContainer from '@/components/layout/TransitionContainer.vue';
import { setDarkMode } from '@/helpers/vuetifyTheme';

/**
 * App shell: top/sidebar nav, splash, and periodic loadAdmin poll.
 * layout is hard-coded to 'top'; sidebar nav appears when Vuetify smAndDown.
 */
export default {
  name: 'Webwalker',

  components: {
    MainNav,
    MainNavSidebar,
    TransitionContainer,
  },

  data: () => ({
    layout: 'top', // 'sidebar' would force sidebar nav; not used in production UI
    interval: null,
    refreshInterval: 30, // seconds between GET /admin polls
    loading: true,
    loaded: false,
  }),

  computed: {
    isSidebarLayout() {
      const display = this.$vuetify && this.$vuetify.display;
      const smAndDown = display ? display.smAndDown : false;
      return this.layout == 'sidebar' || smAndDown;
    },

    isTopLayout() {
      return this.layout == 'top';
    },

    isDarkMode() {
      return this.$store.state.isDarkMode;
    },

    isAuthView() {
      if (this.$route && this.$route.meta) {
        return this.$route.meta.public;
      }
      return false;
    },
  },

  watch: {
    isDarkMode(val) {
      setDarkMode(val);
      this.rootScrollColor();
    },

    $route() {
      this.$store.dispatch('clearErrors');
    },
  },

  mounted() {
    setTimeout(() => {
      this.loading = false;
      setTimeout(() => {
        this.loaded = true;
      }, 250);
    }, 500);

    this.loadData();
    this.interval = setInterval(() => {
      this.loadData();
    }, this.refreshInterval * 1000);
    this.rootScrollColor();
  },

  beforeUnmount() {
    if (this.interval) clearInterval(this.interval);
  },

  methods: {
    /** Refresh admin session + sync Vuetify theme from Vuex. */
    loadData() {
      this.$store.dispatch('loadAdmin');
      setDarkMode(this.$store.state.isDarkMode);
    },

    logout() {
      AuthApi.logout()
        .then(() => {
          localStorage.clear();
          window.location.reload();
        })
        .catch((error) => {
          pushAxiosErrors(this.$store, error);
          localStorage.clear();
        });
    },

    rootScrollColor() {
      const root = document.querySelector(':root');
      if (this.isDarkMode) {
        root.classList.add('scroll-dark');
      } else {
        root.classList.remove('scroll-dark');
      }
    },
  },
};
</script>
